import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  Avatar,
  Stack,
  Badge,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Visibility as ViewIcon,
  School as SchoolIcon,
  AccountTree as AccountTreeIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Grade as GradeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import {
  useGetJobDescriptionsQuery,
  useDeleteJobDescriptionMutation,
  useGetJobDescriptionDepartmentsQuery,
} from '../../redux/jobDescriptionApi';
import type { JobDescription } from './bscTypes';

const JobDescriptionList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJD, setSelectedJD] = useState<JobDescription | null>(null);
  
  // API Hooks
  const { data: jobDescriptions = [], isLoading, refetch } = useGetJobDescriptionsQuery({
    filter: {
      where: { isActive: true },
      order: ['title ASC'],
    },
  });
  const { data: departments = [] } = useGetJobDescriptionDepartmentsQuery();
  const [deleteJD, { isLoading: deleting }] = useDeleteJobDescriptionMutation();
  
  // Filter job descriptions
  const filteredJDs = jobDescriptions.filter((jd) => {
    const matchesSearch = 
      jd.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      jd.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      jd.grade?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = !departmentFilter || jd.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });
  
  const handleDelete = async () => {
    if (selectedJD?.id) {
      await deleteJD({ id: selectedJD.id });
      setDeleteDialogOpen(false);
      setSelectedJD(null);
      refetch();
    }
  };
  
  const openDeleteDialog = (jd: JobDescription) => {
    setSelectedJD(jd);
    setDeleteDialogOpen(true);
  };
  
  const getTotalKRAs = (jd: JobDescription) => {
    return jd.bscPerspectives?.reduce((total, p) => total + (p.kras?.length || 0), 0) || 0;
  };

  const getInitials = (title: string) => {
    return title
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getAvatarColor = (department?: string) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
    ];
    if (!department) return colors[0];
    const index = department.length % colors.length;
    return colors[index];
  };
  
  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, margin: '0 auto', p: { xs: 2, md: 3 } }}>
        {/* Header Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/reviews')} sx={{ color: 'white' }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Job Descriptions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage job descriptions and organizational structure
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/reviews/job-descriptions/new')}
              sx={{ 
                bgcolor: 'white', 
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.9) }
              }}
            >
              New Job Description
            </Button>
          </Box>
        </Paper>

        {/* Stats Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {jobDescriptions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Positions</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="secondary">
                {departments.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Departments</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {jobDescriptions.reduce((sum, jd) => sum + getTotalKRAs(jd), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total KRAs</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {new Set(jobDescriptions.map(jd => jd.grade).filter(Boolean)).size}
              </Typography>
              <Typography variant="body2" color="text.secondary">Grade Levels</Typography>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              placeholder="Search by title, department, or grade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300, flexGrow: 1, maxWidth: 400 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                label="Department"
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(searchTerm || departmentFilter) && (
              <Button 
                size="small" 
                onClick={() => { setSearchTerm(''); setDepartmentFilter(''); }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Paper>
        
        {/* Content */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={48} />
          </Box>
        ) : filteredJDs.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 8, borderRadius: 2 }}>
            <WorkIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || departmentFilter 
                ? 'No job descriptions match your filters'
                : 'No job descriptions found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {searchTerm || departmentFilter 
                ? 'Try adjusting your search criteria'
                : 'Get started by creating your first job description'}
            </Typography>
            {!searchTerm && !departmentFilter && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/reviews/job-descriptions/new')}
              >
                Create Job Description
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredJDs.map((jd) => (
              <Grid item xs={12} sm={6} lg={4} key={jd.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { 
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Header with Avatar */}
                    <Box display="flex" gap={2} mb={2}>
                      <Avatar 
                        sx={{ 
                          bgcolor: getAvatarColor(jd.department),
                          width: 56,
                          height: 56,
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {getInitials(jd.title)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h5" 
                          fontWeight="bold" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '1.25rem',
                          }}
                        >
                          {jd.title}
                        </Typography>
                        {jd.assignedUserName && (
                          <Typography 
                            variant="body1" 
                            color="primary.main"
                            fontWeight="500"
                            sx={{ mt: 0.5 }}
                          >
                            {jd.assignedUserName}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          {jd.department && (
                            <Chip 
                              icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                              label={jd.department} 
                              size="small" 
                              variant="outlined"
                              sx={{ height: 24 }}
                            />
                          )}
                          {jd.grade && (
                            <Chip 
                              label={jd.grade} 
                              size="small" 
                              color="secondary"
                              sx={{ height: 24, fontWeight: 'bold' }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Info Grid */}
                    <Grid container spacing={1}>
                      {jd.reportsTo && (
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccountTreeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Reports to: <strong>{jd.reportsTo}</strong>
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Direct: <strong>{jd.directReportsCount || 0}</strong>
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="disabled" />
                          <Typography variant="body2" color="text.secondary">
                            Indirect: <strong>{jd.indirectReportsCount || 0}</strong>
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {/* Stats Chips */}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                      <Tooltip title="Key Result Areas across all BSC perspectives">
                        <Chip 
                          icon={<AssignmentIcon sx={{ fontSize: 16 }} />}
                          label={`${getTotalKRAs(jd)} KRAs`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                      {jd.minimumQualifications && jd.minimumQualifications.length > 0 && (
                        <Tooltip title="Required qualifications">
                          <Chip 
                            icon={<SchoolIcon sx={{ fontSize: 16 }} />}
                            label={`${jd.minimumQualifications.length} Quals`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {jd.limitsToAuthority && jd.limitsToAuthority.length > 0 && (
                        <Tooltip title="Authority limits defined">
                          <Chip 
                            label={`${jd.limitsToAuthority.length} Limits`}
                            size="small"
                            variant="outlined"
                            color="warning"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => navigate(`/reviews/job-descriptions/${jd.id}`)}
                    >
                      View
                    </Button>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => navigate(`/reviews/job-descriptions/${jd.id}/edit`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => openDeleteDialog(jd)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        {/* Summary */}
        {!isLoading && filteredJDs.length > 0 && (
          <Box mt={3} display="flex" justifyContent="center">
            <Typography variant="body2" color="text.secondary">
              Showing {filteredJDs.length} of {jobDescriptions.length} job descriptions
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            Delete Job Description
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the job description for <strong>"{selectedJD?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Any employees assigned to this position will need to be reassigned.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            color="error" 
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageWithHeader>
  );
};

export default JobDescriptionList;
